import { Box } from "@mui/material"
import { SxProps } from "@mui/system"
import {
  FocusContext,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation"
import React, { useEffect, useState } from "react"

import { DGText } from "../../components/common/DGText"
import { GenericButton } from "../../components/common/GenericButton"
import useTranslate, { Namespaces } from "../../hooks/use-translate"
import { appStore, DGPrompt } from "../../store/AppStore"
import { s } from "../../utilities/common"
import { theme, TI } from "../../utilities/theme"

import { AlertIcon } from "./components/AlertIcon"
import { ErrorIcon } from "./components/ErrorIcon"
import { WifiIcon } from "./components/WifiIcon"

export const PromptContent = ({ prompt }: { prompt: DGPrompt }) => {
  const setPrompt = appStore((state) => state.setPrompt)
  const { focusKey, focusSelf, ref } = useFocusable({
    isFocusBoundary: true,
  })
  const t = useTranslate(Namespaces.Common)

  const [ariaLabel, setAriaLabel] = useState(
    `${prompt?.title ?? "Unbekannter Fehler"}. 
    ${prompt?.message ?? "Unbekannter Fehler"}, 
    ${prompt.confirmButtonText ?? t("confirm__confirm")}`,
  )

  useEffect(() => {
    focusSelf()
  }, [focusSelf])

  return (
    <>
      {/* Gradient */}
      <Box
        sx={{
          height: "100vh",
          width: "100vw",
          zIndex: 2,
          position: "absolute",
          background:
            "linear-gradient(180deg, rgba(21, 31, 49, 0.15) 0%, rgba(21, 31, 49, 0.15) 50.79%, #151F31 92.88%, #151F31 100%), linear-gradient(180deg, rgba(0, 0, 0, 0.45) 0%, rgba(0, 0, 0, 0) 49.87%)",
        }}
      ></Box>
      <Box
        sx={{
          height: "100vh",
          width: "100vw",
          zIndex: 1,
          position: "absolute",
          background: "rgba(0, 0, 0, 0.5)",
        }}
      ></Box>
      {/* Content */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          pl: s("537px"),
          pr: s("537px"),
          zIndex: 3,
        }}
      >
        {prompt.icon === "ALERT" ? (
          <AlertIcon />
        ) : prompt.icon === "ERROR" ? (
          <ErrorIcon />
        ) : prompt.icon === "WIFI" ? (
          <WifiIcon />
        ) : null}
        <DGText
          tx={TI.H05tv}
          sx={{
            color: theme.palette.white.main,
            mt: prompt.icon ? s("40px") : undefined,
            textAlign: "center",
          }}
        >
          {prompt?.title ?? "Unbekannter Fehler"}
        </DGText>
        <DGText
          tx={TI.Subheadline05tv}
          sx={{
            color: theme.palette.white.greyLight,
            mt: s("24px"),
            textAlign: "center",
          }}
        >
          {prompt?.message ?? "Unbekannter Fehler"}
        </DGText>
        <FocusContext.Provider value={focusKey}>
          <Box ref={ref}>
            <ErrorButton
              ariaLabel={ariaLabel}
              onBlur={() =>
                setAriaLabel(prompt.confirmButtonText ?? t("confirm__confirm"))
              }
              data-testid={"confirmbutton"}
              callback={() => {
                if (prompt.onConfirm) prompt.onConfirm()
                setPrompt(undefined)
              }}
              sx={{ mt: s("48px") }}
              title={prompt.confirmButtonText ?? t("confirm__confirm")}
            />
            {prompt.dismissible && (
              <ErrorButton
                data-testid={"cancelbutton"}
                callback={() => {
                  if (prompt.onClose) prompt.onClose()
                  setPrompt(undefined)
                }}
                sx={{ mt: s("48px") }}
                title={prompt.dismissButtonText ?? t("confirm__cancel")}
              />
            )}
            {prompt.additionalButtons &&
              prompt.additionalButtons.length > 0 && (
                <>
                  {prompt.additionalButtons.map((button) => (
                    <ErrorButton
                      focusKey={button.focusKey}
                      callback={button.onPress}
                      sx={{ mt: s("48px") }}
                      title={button.text}
                    />
                  ))}
                </>
              )}
          </Box>
        </FocusContext.Provider>
      </Box>
    </>
  )
}

const ErrorButton = ({
  focusKey,
  title,
  sx,
  callback,
  ...rest
}: {
  focusKey?: string
  title: string
  sx: SxProps
  callback: () => void
  onBlur?: () => void
  ariaLabel?: string
}) => {
  return (
    <GenericButton
      {...rest}
      focusKey={focusKey}
      onEnterPress={callback}
      sx={sx}
    >
      <DGText tx={TI.Subheadline07tv}>{title}</DGText>
    </GenericButton>
  )
}
